// #1 Navigation (DC-32): the brand lockup, the seat's items in the master order (a multi-role person gets the union,
// once) and the signed-in person at the foot. An item a seat does not hold is absent, never disabled (DS #1 D).
// Action Center, Approvals and Coordinator Workbench carry a count badge, read as ", n" after the item's name. The
// active item is aria-current="page" on --nav-active with its icon in --red-text. At ≤ 1180 px (or `rail`) the nav is
// a 76 px icon rail: the names stay for assistive technology and the badge sits on the icon. An item drawn in an icon
// rail may carry its name as a tooltip beside it (`tooltip`, UAT F-146), on hover and on focus, instead of a cut title.

import type { MouseEvent, ReactNode } from "react";
import { Icon, type IconName } from "../core";
import { NAV_BADGES, NAV_ITEM_LABELS, navFor, type NavKey, type SeatKey } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { useOverflow } from "../lib/useOverflow";
import { BrandLockup, type BrandVariant } from "./BrandLockup";
import { SHELL_LABELS } from "./labels";

/** The items a set of seats sees, in the DC-32 master order (the union for a multi-role person). */
export function navForSeats(seats: readonly SeatKey[]): NavKey[] {
  return navFor(seats);
}

/** Each destination's monochrome icon (DC-3, DS #62). */
export const NAV_ICONS: Readonly<Record<NavKey, IconName>> = {
  home: "home",
  actionCenter: "inbox",
  coordinatorWorkbench: "workbench",
  compensationQueue: "dollar",
  requisitions: "reqs",
  candidates: "users",
  pipeline: "pipeline",
  interviews: "interviews",
  debriefs: "debriefs",
  talentCommunity: "pools",
  approvals: "approvals",
  reports: "reports",
  settings: "settings",
};

/** The default link of a destination: "#action-center". */
export function navHref(key: NavKey): string {
  return `#${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

export type NavBadges = Readonly<Partial<Record<NavKey, number>>>;

export interface NavItemProps {
  item: NavKey;
  active: boolean;
  href: string;
  badge?: number | undefined;
  onNavigate?: ((key: NavKey, event: MouseEvent<HTMLAnchorElement>) => void) | undefined;
  hs?: Hs | undefined;
  /** The bottom tab bar's item (smaller words under the icon). */
  bar?: boolean | undefined;
  /** In an icon rail: the name is the item's tooltip, beside it (mirroring the name it already carries). */
  tooltip?: boolean | undefined;
}

/** One destination: an <a> with its icon, its name and, on a badge item, its count. Shared with the bottom tab bar. */
export function NavItem({ item, active, href, badge, onNavigate, hs, bar, tooltip }: NavItemProps) {
  const hot = useHs(hs);
  // The title appears only once the name is measured cut (DS §5's clip rule), so an uncut name has no native tooltip.
  const { ref, overflows } = useOverflow<HTMLSpanElement>();
  const counted = badge !== undefined && badge > 0 && NAV_BADGES.includes(item);
  const link = (
    <a
      className={cx(bar ? "mc-tbar-i" : "mc-nav-item", active && "mc-on")}
      href={href}
      aria-current={active ? "page" : undefined}
      onClick={onNavigate ? (event) => onNavigate(item, event) : undefined}
      {...hot}
    >
      <Icon name={NAV_ICONS[item]} size={bar ? 18 : 20} />
      <span ref={ref} className="mc-nav-t" title={overflows && !tooltip ? NAV_ITEM_LABELS[item] : undefined}>{NAV_ITEM_LABELS[item]}</span>
      {counted ? <span className="mc-nb"><span className="mc-sr">, </span>{badge}</span> : null}
    </a>
  );
  return tooltip ? <Tooltip label={NAV_ITEM_LABELS[item]} mode="mirror" placement="end">{link}</Tooltip> : link;
}

export interface NavProps {
  /** The signed-in person's seats (their union is drawn). */
  seats: readonly SeatKey[];
  /** The destination on screen. */
  active?: NavKey | undefined;
  /** Counts for the badge items (Action Center, Approvals, Coordinator Workbench); 0 or absent draws none. */
  badges?: NavBadges | undefined;
  /** Each destination's link (default navHref). */
  href?: ((key: NavKey) => string) | undefined;
  onNavigate?: ((key: NavKey, event: MouseEvent<HTMLAnchorElement>) => void) | undefined;
  brand?: BrandVariant | undefined;
  /** The nav foot: the signed-in person (#4). */
  foot?: ReactNode | undefined;
  /** The 76 px icon rail at any width (it is automatic at ≤ 1180 px). */
  rail?: boolean | undefined;
  /** Capture hotspots per destination. */
  hs?: Readonly<Partial<Record<NavKey, Hs>>> | undefined;
  className?: string | undefined;
}

export function Nav({ seats, active, badges, href = navHref, onNavigate, brand, foot, rail, hs, className }: NavProps) {
  const items = navForSeats(seats);
  return (
    <nav className={cx("mc-nav", rail && "mc-nav-rail", className)} aria-label={SHELL_LABELS.navigation}>
      <BrandLockup variant={brand} rail={rail} />
      <ul className="mc-nav-list">
        {items.map((item) => (
          <li key={item}>
            <NavItem item={item} active={item === active} href={href(item)} badge={badges?.[item]} onNavigate={onNavigate} hs={hs?.[item]} />
          </li>
        ))}
      </ul>
      {foot ? <div className="mc-nav-foot">{foot}</div> : null}
    </nav>
  );
}
